import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, KeyRound, Wallet } from "lucide-react";
import WalletBlock from "./WalletBlock";
import { ROLE_LABEL, ROLE_STYLE, STATE_LABEL, STATE_STYLE, calls, fmt, inr } from "./format";

/**
 * One vendor, collapsed by default (Oct 2026) — a balance and a 30-day spend do
 * not change while you read them, so the header carries both and the detail
 * stays one click away. "Set" opens the card and lands you on the input.
 */
export default function VendorCard({ vendor, onReload }) {
  const [open, setOpen] = useState(false);
  const [focusWallet, setFocusWallet] = useState(false);
  const bodyRef = useRef(null);
  const degraded = vendor.degraded_reason;
  const inChain = vendor.apis.some((a) => a.role === "primary" || a.role === "secondary");
  const wallet = vendor.wallet;
  const low = wallet?.tracked && wallet.balance_inr <= wallet.low_threshold_inr;

  useEffect(() => {
    if (!open) setFocusWallet(false);
  }, [open]);

  const openAtWallet = (e) => {
    e.stopPropagation();
    setOpen(true);
    setFocusWallet(true);
  };

  return (
    <div
      className={`bg-white rounded-xl border transition-shadow hover:shadow-sm ${
        degraded ? "border-red-200" : low ? "border-amber-300" : inChain ? "border-slate-200/80" : "border-slate-200/60"
      }`}
      data-testid={`vendor-${vendor.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 flex-wrap p-4 text-left rounded-xl hover:bg-slate-50/60 transition-colors"
        data-testid={`vendor-${vendor.id}-toggle`}
      >
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-base font-semibold text-slate-900">{vendor.label}</h4>
            {degraded ? (
              <span
                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 border border-red-200"
                data-testid={`vendor-${vendor.id}-degraded`}
              >
                <AlertTriangle className="w-3 h-3" />
                {String(degraded).replace(/_/g, " ").toUpperCase()}
              </span>
            ) : vendor.configured ? (
              <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200">
                <CheckCircle2 className="w-3 h-3" /> CONNECTED
              </span>
            ) : (
              <span
                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200"
                data-testid={`vendor-${vendor.id}-unconfigured`}
              >
                <KeyRound className="w-3 h-3" /> KEY NOT SET
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {vendor.apis.length} API{vendor.apis.length === 1 ? "" : "s"} · {vendor.owner}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-5 flex-wrap">
          <div className="text-right">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
              Balance
            </div>
            <div
              className={`text-lg font-bold leading-tight ${low ? "text-amber-700" : "text-slate-900"}`}
              data-testid={`vendor-${vendor.id}-header-balance`}
            >
              {wallet?.tracked ? `₹${fmt(Math.round(wallet.balance_inr))}` : "—"}
            </div>
            <div className="text-[11px] text-slate-400">
              {wallet?.tracked ? `${fmt(wallet.lookups_left)} lookups left` : "post-paid"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
              Usage · 30 days
            </div>
            <div
              className="text-lg font-bold text-slate-900 leading-tight"
              data-testid={`vendor-${vendor.id}-spend`}
            >
              ₹{inr(vendor.spend_30d_inr)}
            </div>
            <div className="text-[11px] text-slate-400">{calls(vendor.calls_30d)}</div>
          </div>
          {wallet && (
            <span
              role="button"
              tabIndex={0}
              onClick={openAtWallet}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openAtWallet(e)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white rounded-md border border-slate-200 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
              data-testid={`vendor-${vendor.id}-set-balance`}
            >
              <Wallet className="w-3 h-3" /> Set balance
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4" ref={bodyRef} data-testid={`vendor-${vendor.id}-body`}>
          <WalletBlock vendor={vendor} onReload={onReload} autoFocus={focusWallet} />

          <div className="mt-3 space-y-1.5" data-testid={`vendor-${vendor.id}-apis`}>
            {vendor.apis.map((api) => {
              const u24 = api.usage?.["24h"];
              const u30 = api.usage?.["30d"];
              return (
                <div
                  key={api.id}
                  className="flex items-start gap-3 px-3 py-2 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-colors"
                  data-testid={`api-${api.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{api.name}</span>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ROLE_STYLE[api.role] || ROLE_STYLE.off}`}
                        data-testid={`api-${api.id}-role`}
                      >
                        {ROLE_LABEL[api.role] || api.role}
                      </span>
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${STATE_STYLE[api.state] || STATE_STYLE.pending}`}
                      >
                        {STATE_LABEL[api.state] || api.state}
                      </span>
                      {api.scope && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                          {api.scope}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{api.note}</p>
                    <p className="text-[11px] text-slate-400">{api.billing}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-700">
                      {api.price_inr ? `₹${api.price_inr}` : "—"}
                      {api.price_assumed && <span className="text-[10px] text-amber-600"> est</span>}
                    </div>
                    {u30?.calls ? (
                      <div className="text-[11px] text-slate-400" data-testid={`api-${api.id}-usage`}>
                        {calls(u30.calls)} · ₹{inr(u30.inr)} · 30d
                        {u24?.calls ? (
                          <>
                            <br />
                            {u24.success_rate_pct}% answered today
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-300">no calls</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
